/**
 * Планировщик приёмов лекарств.
 *
 * Модуль разворачивает правило курса в конкретные дозы. Все функции чистые:
 * на вход — карточка препарата и границы горизонта, на выход — список приёмов.
 * Ни базы, ни текущего времени внутри нет, поэтому поведение детерминировано и
 * проверяется тестами.
 *
 * ## Почему дозы материализуются, а не вычисляются на лету
 *
 * Режим дня (сон, еда) считается по правилам при каждом рендере — там нет
 * состояния. У дозы состояние есть: принята, пропущена, отложена, причина
 * пропуска. Хранить его негде, кроме как в самой дозе. Плюс локальное
 * уведомление нужно завести на конкретное время заранее. Поэтому планировщик
 * порождает `DoseEvent` на горизонт вперёд (30 дней), а история остаётся в базе
 * даже после изменения расписания.
 *
 * ## Про редактирование курса
 *
 * `planDoseSync` сравнивает расписание с тем, что уже лежит в базе, и трогает
 * только неотмеченные дозы будущего. Уже принятые и пропущенные приёмы не
 * удаляются никогда — иначе правка времени приёма стёрла бы журнал.
 */

import type { DoseEvent, Medication } from '@/shared/db/schema';
import { hasWeekday } from '@/shared/lib/weekdays';

import { hasDoseSchedule } from './course';
import { isDoseAhead } from './dose-status';
import {
  addDays,
  dateRange,
  diffDays,
  type IsoDate,
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  type MinutesOfDay,
  weekdayIndex,
} from '@/shared/lib/datetime';

/** На сколько дней вперёд материализуются приёмы. */
export const DOSE_HORIZON_DAYS = 30;

/** Время первого приёма по умолчанию, если пользователь не выбрал своё. */
const DEFAULT_FIRST_DOSE_MIN = 9 * MINUTES_IN_HOUR;

/** Окно бодрствования, внутри которого распределяются приёмы. */
const WAKING_START_MIN = 8 * MINUTES_IN_HOUR;
const WAKING_END_MIN = 22 * MINUTES_IN_HOUR;

/**
 * Приём расписания: время и его источник.
 *
 * Источник называется явно, потому что у привязанного к еде курса время —
 * величина непостоянная. Сдвинули завтрак — время уехало, и узнать в новом
 * приёме прежний по одному числу уже нельзя.
 */
export interface ScheduleSlot {
  scheduledMin: MinutesOfDay;
  /** Еда, из которой посчитано время. `null` — время задано часами. */
  mealRoutineId: string | null;
}

/** Запланированный приём — заготовка для записи `DoseEvent`. */
export interface PlannedDose {
  medicationId: string;
  date: IsoDate;
  scheduledMin: MinutesOfDay;
  /** Еда, из которой посчитано время. `null` — время задано часами. */
  mealRoutineId: string | null;
  plannedAmount: number;
}

/* ═══════════════════════ Границы курса ═══════════════════════ */

/** Последний день курса. `null` — курс без конца (бессрочный или «по необходимости»). */
export function courseEndDate(medication: Medication): IsoDate | null {
  switch (medication.courseType) {
    case 'untilDate':
      return medication.endDate;
    case 'daysCount': {
      const days = medication.durationDays ?? 0;
      // День старта входит в курс, поэтому «7 дней» заканчиваются на шестой день после начала.
      return days > 0 ? addDays(medication.startDate, days - 1) : medication.startDate;
    }
    case 'indefinite':
    case 'asNeeded':
    case 'none':
      return null;
  }
}

/** Идёт ли курс в этот день (без учёта расписания внутри курса). */
export function isCourseActiveOn(medication: Medication, date: IsoDate): boolean {
  if (date < medication.startDate) return false;
  const end = courseEndDate(medication);
  return end === null || date <= end;
}

/** Сколько дней курса осталось, включая сегодняшний. `null` — курс бессрочный. */
export function courseDaysLeft(medication: Medication, today: IsoDate): number | null {
  const end = courseEndDate(medication);
  if (end === null) return null;
  if (today > end) return 0;
  const from = today < medication.startDate ? medication.startDate : today;
  return diffDays(from, end) + 1;
}

/**
 * Курс, который уже закончился, но ещё не убран в архив.
 *
 * Это состояние вычисляется, а не хранится, и архивацию не подменяет. Архив —
 * решение человека: курс могли продлить, дату могли поставить с ошибкой, и
 * прятать карточку самостоятельно приложение не вправе. Признак нужен только
 * для того, чтобы завершённые курсы не оседали среди действующих: иначе через
 * полгода в «Активных» придётся выискивать те два препарата, которые
 * принимаются сейчас.
 *
 * В последний день курс считается законченным не по календарю, а по делу:
 * когда принимать больше нечего. Отметив последнюю таблетку, человек закончил
 * лечение — и ждать полуночи, чтобы приложение это признало, странно.
 *
 * `doses` — приёмы, известные экрану. Без них последний день считается
 * незаконченным: отсутствие записей означает, что расписание ещё не
 * материализовано, а не что принимать нечего.
 */
/**
 * Отпечаток полей, влияющих на расписание.
 *
 * Нужен, чтобы материализация запускалась при правке курса и не запускалась от
 * любой другой записи в препарат — например, от списания остатка при отметке
 * «Принял».
 */
export function scheduleFingerprint(medication: Medication): string {
  return [
    medication.id,
    medication.isActive ? '1' : '0',
    medication.archivedAt ?? '',
    medication.courseType,
    medication.startDate,
    medication.endDate ?? '',
    medication.durationDays ?? '',
    medication.scheduleType,
    medication.timesPerDay,
    medication.intervalHours,
    medication.weekdaysMask,
    medication.intervalDays,
    medication.cycleActiveDays,
    medication.cyclePauseDays,
    medication.doseTimes.join(','),
    medication.doseAmount,
    // Привязка к еде: у такого курса время считается из режима дня, а не из
    // `doseTimes`, и смена привязки меняет расписание, не трогая времена.
    (medication.mealRoutineIds ?? []).join(','),
    medication.mealRelation,
    medication.mealOffsetMin,
  ].join(':');
}

export function isCourseFinished(
  medication: Medication,
  today: IsoDate,
  doses: readonly DoseEvent[] = [],
): boolean {
  if (!medication.isActive || medication.archivedAt !== null) return false;

  const end = courseEndDate(medication);
  if (end === null) return false;
  if (today > end) return true;
  if (today < end) return false;

  // Последний день не по расписанию — принимать в него и не планировалось.
  if (!isScheduledDay(medication, today)) return true;

  const remaining = doses.filter(
    (dose) => dose.medicationId === medication.id && dose.date === today && dose.deletedAt === null,
  );

  return remaining.length > 0 && !remaining.some(isDoseAhead);
}

/**
 * Относится ли приём к текущему прохождению курса.
 *
 * Журнал приёмов переживает и правку курса, и его повтор: записи прошлого
 * прохождения остаются в базе как история болезни. Но прогресс «принято 12 из
 * 30» должен считаться по нынешнему курсу — иначе после повтора счётчик
 * продолжил бы расти с прошлого раза, а знаменатель начался бы заново.
 */
export function isDoseWithinCourse(medication: Medication, dose: DoseEvent): boolean {
  if (dose.date < medication.startDate) return false;
  const end = courseEndDate(medication);
  return end === null || dose.date <= end;
}

/** Длина курса в днях, включая день старта. `null` — у курса нет конца. */
export function courseLengthDays(medication: Medication): number | null {
  const end = courseEndDate(medication);
  if (end === null) return null;
  return diffDays(medication.startDate, end) + 1;
}

/**
 * Изменения, продлевающие курс на `days` дней, считая от сегодняшнего дня.
 *
 * Отсчёт от сегодня, а не от прежнего конца: человек продлевает курс, чтобы
 * принимать лекарство дальше, и досиживать просроченные дни ему незачем. День
 * старта не трогается — он часть истории, по нему считается пройденное;
 * переносить его значило бы потерять журнал курса.
 *
 * Без явного числа берётся прежняя длительность курса — самый частый случай:
 * «пропил десять дней, пропью ещё десять».
 *
 * `null` — продлевать нечего: у курса нет конца. Идущий курс отсюда не
 * исключён намеренно: расчёт от сегодняшнего дня не может его укоротить, а
 * решение, кому предлагать продление, принимает экран.
 */
export function planCourseExtension(
  medication: Medication,
  today: IsoDate,
  days?: number,
): Pick<Medication, 'durationDays' | 'endDate'> | null {
  const length = courseLengthDays(medication);
  if (length === null || length <= 0) return null;

  const span = Math.round(days ?? length);
  if (!Number.isFinite(span) || span <= 0) return null;

  const newEnd = addDays(today, span - 1);

  return medication.courseType === 'daysCount'
    ? { durationDays: diffDays(medication.startDate, newEnd) + 1, endDate: medication.endDate }
    : { durationDays: medication.durationDays, endDate: newEnd };
}

/**
 * Изменения, начинающие тот же курс заново с дня `start`.
 *
 * Отличие от продления принципиальное. Продление растягивает нынешний курс:
 * счётчик принятых продолжает расти, знаменатель увеличивается, история одна.
 * Повтор — это новое прохождение: день старта переносится на `start`, и
 * прогресс считается с нуля, потому что приёмы прошлого раза остаются за
 * границей курса. Сами записи никуда не деваются — они видны в календаре и в
 * истории дня, как и положено медицинскому журналу.
 *
 * Перенос старта заодно перезапускает интервальные схемы: «через день» и
 * циклы 21/7 отсчитываются от начала курса, и новый круг должен начинаться
 * с первого дня, а не подхватывать фазу прошлого.
 *
 * Обычно `start` — сегодняшний день, но может быть и прошедшим: курс начали,
 * а записать не успели. Приёмы пройденных дней достраивает материализация
 * (`pastCourseGap` говорит, каких дней недостаёт).
 *
 * `null` — повторять нечего: у курса нет конца, он и так идёт бессрочно.
 */
export function planCourseRestart(
  medication: Medication,
  start: IsoDate,
  days?: number,
): Pick<Medication, 'startDate' | 'durationDays' | 'endDate'> | null {
  const length = courseLengthDays(medication);
  if (length === null || length <= 0) return null;

  const span = Math.round(days ?? length);
  if (!Number.isFinite(span) || span <= 0) return null;

  return medication.courseType === 'daysCount'
    ? { startDate: start, durationDays: span, endDate: medication.endDate }
    : {
        startDate: start,
        durationDays: medication.durationDays,
        endDate: addDays(start, span - 1),
      };
}

/**
 * Прошедшие дни курса, приёмов которых ещё нет.
 *
 * Расписание строится от сегодняшнего дня вперёд, и курс, начатый задним
 * числом — вчера, на прошлой неделе, — терял пройденные дни: счёт «принято
 * N из M» не мог дойти до конца. Эти дни достраиваются отдельно, и граница у
 * них одна на форму и на запись.
 *
 * `builtFrom` — день, с которого приёмы уже строились: прежнее начало курса или
 * сегодняшний день. Дни с него не трогаются: там журнал уже ведётся.
 * `null` — достраивать нечего.
 */
export function pastCourseGap(
  startDate: IsoDate,
  builtFrom: IsoDate,
  today: IsoDate,
): { from: IsoDate; to: IsoDate } | null {
  const to = addDays(builtFrom < today ? builtFrom : today, -1);
  return startDate <= to ? { from: startDate, to } : null;
}

/* ═══════════════════════ Расписание ═══════════════════════ */

/**
 * Попадает ли день в расписание приёма.
 *
 * Все интервальные типы отсчитываются от даты начала курса, а не от «сегодня»:
 * иначе схема «через день» сбивалась бы при каждом открытии экрана.
 */
export function isScheduledDay(medication: Medication, date: IsoDate): boolean {
  if (!isCourseActiveOn(medication, date)) return false;

  switch (medication.scheduleType) {
    case 'timesPerDay':
    case 'everyNHours':
      return true;

    case 'weekdays':
      return hasWeekday(medication.weekdaysMask, weekdayIndex(date));

    case 'everyNDays': {
      const step = Math.max(1, Math.round(medication.intervalDays));
      return diffDays(medication.startDate, date) % step === 0;
    }

    case 'cycle': {
      const active = Math.max(1, Math.round(medication.cycleActiveDays));
      const pause = Math.max(0, Math.round(medication.cyclePauseDays));
      const period = active + pause;
      return diffDays(medication.startDate, date) % period < active;
    }
  }
}

/**
 * Времена приёма по умолчанию для N раз в день.
 *
 * До четырёх приёмов используются привычные схемы (утро / день / вечер), дальше
 * приёмы равномерно распределяются по времени бодрствования: будить человека
 * ради таблетки — не то, что стоит предлагать по умолчанию.
 */
export function defaultDoseTimes(count: number): MinutesOfDay[] {
  const total = Math.max(1, Math.round(count));

  const presets: Record<number, MinutesOfDay[]> = {
    1: [9 * MINUTES_IN_HOUR],
    2: [9 * MINUTES_IN_HOUR, 21 * MINUTES_IN_HOUR],
    3: [8 * MINUTES_IN_HOUR, 14 * MINUTES_IN_HOUR, 20 * MINUTES_IN_HOUR],
    4: [8 * MINUTES_IN_HOUR, 12 * MINUTES_IN_HOUR, 16 * MINUTES_IN_HOUR, 20 * MINUTES_IN_HOUR],
  };

  const preset = presets[total];
  if (preset) return preset;

  const step = (WAKING_END_MIN - WAKING_START_MIN) / (total - 1);
  return Array.from({ length: total }, (_, index) => Math.round(WAKING_START_MIN + step * index));
}

/** Сортировка, отсев дублей и выход за границы суток. */
function normalizeTimes(times: readonly MinutesOfDay[]): MinutesOfDay[] {
  const unique = new Set<MinutesOfDay>();
  for (const time of times) {
    if (!Number.isFinite(time)) continue;
    const normalized = ((Math.round(time) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    unique.add(normalized);
  }
  return [...unique].sort((a, b) => a - b);
}

/**
 * Сетка приёмов для схемы «каждые N часов».
 *
 * Сетка строится внутри суток от опорного времени и повторяется каждый день.
 * Альтернатива — непрерывный отсчёт от первой дозы — даёт дрейф: при интервале
 * 5 часов приём каждый день сдвигался бы на час, и расписание переставало быть
 * предсказуемым.
 */
function everyNHoursTimes(medication: Medication): MinutesOfDay[] {
  const anchor = medication.doseTimes[0] ?? DEFAULT_FIRST_DOSE_MIN;
  const stepMin = Math.max(1, Math.min(24, Math.round(medication.intervalHours))) * MINUTES_IN_HOUR;

  const times: MinutesOfDay[] = [];
  for (let offset = 0; offset < MINUTES_IN_DAY; offset += stepMin) {
    times.push(anchor + offset);
  }
  return normalizeTimes(times);
}

/**
 * Времена приёма внутри дня.
 *
 * Для «N раз в день» ведущим считается число приёмов: если явных времён меньше,
 * недостающие берутся из схемы по умолчанию, лишние отбрасываются.
 */
export function doseTimesOf(medication: Medication): MinutesOfDay[] {
  if (!hasDoseSchedule(medication)) return [];
  if (medication.scheduleType === 'everyNHours') return everyNHoursTimes(medication);

  const explicit = normalizeTimes(medication.doseTimes);

  if (medication.scheduleType === 'timesPerDay') {
    const target = Math.max(1, Math.round(medication.timesPerDay));
    if (explicit.length >= target) return explicit.slice(0, target);

    const filled = defaultDoseTimes(target);
    explicit.forEach((time, index) => {
      filled[index] = time;
    });
    return normalizeTimes(filled);
  }

  if (explicit.length > 0) return explicit;
  return defaultDoseTimes(Math.max(1, Math.round(medication.timesPerDay)));
}

/** Сколько приёмов приходится на день по расписанию. */
export function dosesPerDay(medication: Medication): number {
  return doseTimesOf(medication).length;
}

/* ═══════════════════════ Генерация доз ═══════════════════════ */

/** Учитывается ли препарат планировщиком. */
export function isSchedulable(medication: Medication): boolean {
  return (
    medication.deletedAt === null &&
    medication.archivedAt === null &&
    medication.isActive &&
    // Без расписания планировать нечего: приём по факту потребности и запас в
    // аптечке приёмов не порождают.
    hasDoseSchedule(medication)
  );
}

/**
 * Разворачивает расписание в приёмы на отрезке `[from, to]`.
 * Диапазон автоматически обрезается границами курса.
 */
/**
 * Приёмы расписания на конкретный день: время и его источник.
 *
 * Курс, привязанный к еде, времени в себе не хранит: оно считается из режима
 * этого дня. Отсюда и параметр — вызывающая сторона знает, как достать режим,
 * а расписание приёмов о базе знать не должно. Без него берутся записанные часы,
 * и это ровно прежнее поведение.
 */
export type ScheduleSlotsForDate = (date: IsoDate) => ScheduleSlot[];

export function generateDoses(
  medication: Medication,
  from: IsoDate,
  to: IsoDate,
  slotsForDate?: ScheduleSlotsForDate,
): PlannedDose[] {
  if (!isSchedulable(medication)) return [];

  const start = from < medication.startDate ? medication.startDate : from;
  const end = courseEndDate(medication);
  const last = end !== null && end < to ? end : to;
  if (last < start) return [];

  const fixed: ScheduleSlot[] = doseTimesOf(medication).map((scheduledMin) => ({
    scheduledMin,
    mealRoutineId: null,
  }));
  const amount = medication.doseAmount > 0 ? medication.doseAmount : 1;
  const doses: PlannedDose[] = [];

  for (const date of dateRange(start, last)) {
    if (!isScheduledDay(medication, date)) continue;

    // Пустой список — не сбой: в этот день нет того приёма пищи, к которому
    // привязан курс, и ставить приём не от чего.
    for (const slot of slotsForDate ? slotsForDate(date) : fixed) {
      doses.push({
        medicationId: medication.id,
        date,
        scheduledMin: slot.scheduledMin,
        mealRoutineId: slot.mealRoutineId,
        plannedAmount: amount,
      });
    }
  }

  return doses;
}

/**
 * Общее число приёмов за весь курс — знаменатель прогресса «принято 12 из 30».
 * Для бессрочного курса и приёма по необходимости возвращает `null`.
 */
export function countCourseDoses(medication: Medication): number | null {
  if (!isSchedulable(medication)) return null;
  const end = courseEndDate(medication);
  if (end === null) return null;

  const perDay = dosesPerDay(medication);
  if (perDay === 0) return null;

  let days = 0;
  for (const date of dateRange(medication.startDate, end)) {
    if (isScheduledDay(medication, date)) days += 1;
  }
  return days * perDay;
}

/* ═══════════════════ Синхронизация с базой ═══════════════════ */

export interface DoseSyncPlan {
  /** Приёмы, которых ещё нет в базе. */
  create: PlannedDose[];
  /** Неотмеченные приёмы будущего, выпавшие из расписания после правки курса. */
  obsolete: DoseEvent[];
  /**
   * Приёмы, у которых уехало время: еда сдвинулась, а приём тот же самый.
   *
   * Отдельно от «удалить и создать заново» — потому что это не другой приём.
   * Пересоздание теряет всё, что к нему прилипло: отметку «отложить», заметку,
   * заведённое уведомление, — и в журнале один приём выглядел бы двумя.
   */
  move: { dose: DoseEvent; scheduledMin: MinutesOfDay; mealRoutineId: string | null }[];
}

function doseKey(dose: { date: IsoDate; scheduledMin: MinutesOfDay }): string {
  return `${dose.date}#${dose.scheduledMin}`;
}

/**
 * Чем приём опознаётся при сверке с расписанием.
 *
 * У привязанного к еде приёма это день и сама еда: время у него меняется вместе
 * с завтраком, и опознавать по времени — значит терять приём при каждом сдвиге.
 * У остальных опорой остаётся время: другого признака у них и нет.
 */
function slotKey(dose: {
  date: IsoDate;
  scheduledMin: MinutesOfDay;
  mealRoutineId?: string | null;
}): string {
  return dose.mealRoutineId ? `${dose.date}#еда:${dose.mealRoutineId}` : doseKey(dose);
}

/**
 * Порядок внутри группы одинаковых приёмов: сначала то, что трогал человек.
 *
 * Отметка «принято» или «пропущено» — это журнал, и она должна пережить
 * чистку дублей при любом раскладе. Среди равных выбор идёт по времени
 * создания, чтобы результат не зависел от порядка выборки из базы.
 */
function journalFirst(a: DoseEvent, b: DoseEvent): number {
  const marked = Number(a.status !== 'pending') - Number(b.status !== 'pending');
  if (marked !== 0) return -marked;
  return a.createdAt - b.createdAt;
}

/**
 * Считает разницу между расписанием и содержимым базы на горизонте.
 *
 * Из базы удаляются только дозы со статусом `pending`, начиная с `from`:
 * прошедшие и уже отмеченные приёмы — это журнал, он неприкосновенен.
 *
 * Приёмы опознаются по слоту, а не по времени. У курса, привязанного к еде,
 * время меняется вместе с завтраком: сверка по времени видела на новом месте
 * «недостающий» приём, заводила его — и рядом с уже отмеченным утренним
 * появлялся второй, на который человек ничего не принимал. Теперь приём
 * помнит свою еду, поэтому сдвиг завтрака двигает предстоящий приём и не
 * трогает отмеченный: за день лекарство принято один раз, а не два.
 *
 * Кроме приёмов, выпавших из расписания, отсюда уходят точные дубли — две
 * записи об одном приёме в один день и то же время. Расписание таких породить
 * не может: времена внутри дня приводятся к уникальным. Появлялись они, когда
 * синхронизация запускалась дважды одновременно, и сами по себе не исчезали:
 * дубль присутствует в расписании, поэтому прежняя проверка «нет в плане» его
 * не замечала, и человек до конца курса видел один и тот же приём дважды — и в
 * таблетнице, и в календаре. Синхронизация теперь не просто не добавляет
 * лишнего, а приводит базу к правильному состоянию.
 */
export function planDoseSync(
  medication: Medication,
  existing: DoseEvent[],
  from: IsoDate,
  to: IsoDate,
  slotsForDate?: ScheduleSlotsForDate,
): DoseSyncPlan {
  const planned = generateDoses(medication, from, to, slotsForDate);

  const alive = existing.filter(
    (dose) => dose.deletedAt === null && dose.medicationId === medication.id,
  );

  const byKey = new Map<string, DoseEvent[]>();
  for (const dose of alive) {
    const key = slotKey(dose);
    const group = byKey.get(key);
    if (group) group.push(dose);
    else byKey.set(key, [dose]);
  }

  /** Приём, который представляет свой слот; остальные из группы — лишние. */
  const primary = new Map<string, DoseEvent>();
  const duplicates: DoseEvent[] = [];

  for (const [key, group] of byKey) {
    const sorted = [...group].sort(journalFirst);
    primary.set(key, sorted[0]!);
    // Первый остаётся, остальные — лишние. Отмеченные приёмы не удаляются
    // никогда: если человек по ошибке отметил оба дубля, пусть лучше в журнале
    // останется лишняя запись, чем пропадёт настоящая.
    for (const dose of sorted.slice(1)) {
      if (dose.date >= from && isDoseAhead(dose)) duplicates.push(dose);
    }
  }

  const create: PlannedDose[] = [];
  const move: DoseSyncPlan['move'] = [];
  const matched = new Set<string>();

  for (const slot of planned) {
    /*
      Приёмы, заведённые до появления связи с едой, опознаются по времени —
      другого признака у них нет. Пока еду не двигали, время приёма и время
      слота совпадают, поэтому первая же сверка находит их и проставляет связь.
    */
    const legacy = slot.mealRoutineId === null ? undefined : primary.get(doseKey(slot));
    const dose = primary.get(slotKey(slot)) ?? legacy;

    if (!dose || matched.has(dose.id)) {
      create.push(slot);
      continue;
    }

    matched.add(dose.id);

    const journal = !isDoseAhead(dose);
    // Отметка остаётся на том времени, когда приём действительно был: сдвиг
    // завтрака не переписывает историю, он касается только предстоящего.
    const scheduledMin = journal ? dose.scheduledMin : slot.scheduledMin;

    if (scheduledMin !== dose.scheduledMin || (dose.mealRoutineId ?? null) !== slot.mealRoutineId) {
      move.push({ dose, scheduledMin, mealRoutineId: slot.mealRoutineId });
    }
  }

  const removedIds = new Set(duplicates.map((dose) => dose.id));

  return {
    create,
    move,
    obsolete: [
      ...alive.filter(
        (dose) =>
          dose.date >= from &&
          // Отложенные тоже: выпав из расписания, они иначе оставались бы
          // в календаре навсегда.
          isDoseAhead(dose) &&
          !matched.has(dose.id) &&
          !removedIds.has(dose.id),
      ),
      ...duplicates,
    ],
  };
}

/* ═══════════════════════ Остаток упаковки ═══════════════════════ */

/** Сколько единиц препарата уходит за сутки по расписанию. */
export function dailyConsumption(medication: Medication): number {
  const amount = medication.doseAmount > 0 ? medication.doseAmount : 1;
  return dosesPerDay(medication) * amount;
}

/**
 * На сколько дней хватит остатка упаковки.
 * `null` — учёт остатка не ведётся или расписание не расходует запас.
 */
export function stockDaysLeft(medication: Medication): number | null {
  if (medication.stockQuantity === null) return null;
  const perDay = dailyConsumption(medication);
  if (perDay <= 0) return null;
  return Math.floor(Math.max(0, medication.stockQuantity) / perDay);
}

/**
 * Пора ли покупать: остаток опустился до порога напоминания.
 *
 * Расписание для этого не требуется. Раньше требовалось — и лекарство «по
 * необходимости», которого осталась одна таблетка, молчало до того дня, когда
 * оно понадобится; то же самое было бы и с аптечкой, где расписания нет ни у
 * чего. Порог человек задаёт сам, а без остатка вопрос не стоит вовсе.
 */
export function needsRefill(medication: Medication): boolean {
  if (medication.stockQuantity === null) return false;
  if (medication.deletedAt !== null || medication.archivedAt !== null || !medication.isActive) {
    return false;
  }
  return medication.stockQuantity <= Math.max(0, medication.refillThreshold);
}

/** Порог напоминания по умолчанию — запас на три дня приёма. */
export function suggestedRefillThreshold(medication: Medication): number {
  return Math.max(1, Math.ceil(dailyConsumption(medication) * 3));
}

/* ═══════════════════════ Ближайший приём ═══════════════════════ */

/** Первый приём, который ещё предстоит. Используется в шапке вкладки. */
export function findNextDose(
  doses: DoseEvent[],
  today: IsoDate,
  nowMin: MinutesOfDay,
): DoseEvent | null {
  let next: DoseEvent | null = null;

  for (const dose of doses) {
    if (dose.deletedAt !== null) continue;
    if (dose.status !== 'pending' && dose.status !== 'postponed') continue;
    if (dose.date < today) continue;
    if (dose.date === today && dose.scheduledMin < nowMin) continue;

    if (
      next === null ||
      dose.date < next.date ||
      (dose.date === next.date && dose.scheduledMin < next.scheduledMin)
    ) {
      next = dose;
    }
  }

  return next;
}
